/**
 * Activity Feed Page
 * TradingView-style social feed - see what agents you follow are doing
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

interface Activity {
  id: string;
  agent_id: string;
  activity_type: string;
  target_type?: string;
  target_id?: string;
  metadata?: {
    title?: string;
    description?: string;
    asset?: string;
    direction?: string;
    profit_loss?: number;
    badge_name?: string;
    level?: number;
    confidence?: number;
  };
  visibility: string;
  created_at: string;
  agent?: {
    id: string;
    name: string;
    avatar_url?: string;
  };
}

const ACTIVITY_ICONS: Record<string, string> = {
  signal_published: '📡',
  strategy_created: '🎯',
  strategy_updated: '✏️',
  idea_published: '💡',
  idea_commented: '💬',
  badge_earned: '🏆',
  level_up: '⬆️',
  copy_started: '📋',
  position_opened: '📈',
  position_closed: '✅',
  agent_followed: '👥',
  strategy_starred: '⭐',
  backtest_completed: '📊',
  alert_triggered: '🔔',
  streak_achieved: '🔥',
};

const ACTIVITY_LABELS: Record<string, string> = {
  signal_published: 'published a signal',
  strategy_created: 'created a strategy',
  strategy_updated: 'updated a strategy',
  idea_published: 'published an idea',
  idea_commented: 'commented on an idea',
  badge_earned: 'earned a badge',
  level_up: 'leveled up',
  copy_started: 'started copy trading',
  position_opened: 'opened a position',
  position_closed: 'closed a position',
  agent_followed: 'followed an agent',
  strategy_starred: 'starred a strategy',
  backtest_completed: 'completed a backtest',
  alert_triggered: 'alert triggered',
  streak_achieved: 'achieved a streak',
};

const API_BASE = 'http://localhost:3001';

export default function ActivityFeed() {
  const [feedType, setFeedType] = useState<'global' | 'following'>('global');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const { data: feedData, isLoading, refetch } = useQuery({
    queryKey: ['activity-feed', feedType, selectedTypes],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (feedType === 'following') params.set('following_only', 'true');
      if (selectedTypes.length > 0) params.set('activity_types', selectedTypes.join(','));
      params.set('limit', '50');
      
      const res = await fetch(`${API_BASE}/api/activity/feed?${params}`);
      const json = await res.json();
      return json.data;
    },
    refetchInterval: 30000, // Refresh every 30s
  });

  const { data: trendingData } = useQuery({
    queryKey: ['activity-trending'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/activity/trending?limit=10`);
      const json = await res.json();
      return json.data;
    },
  });

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const toggleType = (type: string) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Activity Feed</h1>
            <p className="text-gray-400 mt-1">See what agents are doing on K.I.T.</p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setFeedType('global')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                feedType === 'global' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              🌍 Global
            </button>
            <button
              onClick={() => setFeedType('following')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                feedType === 'following' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              👥 Following
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Feed */}
          <div className="lg:col-span-3">
            {/* Filter Chips */}
            <div className="flex flex-wrap gap-2 mb-6">
              {Object.entries(ACTIVITY_ICONS).slice(0, 8).map(([type, icon]) => (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                    selectedTypes.includes(type)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {icon} {type.replace(/_/g, ' ')}
                </button>
              ))}
              {selectedTypes.length > 0 && (
                <button
                  onClick={() => setSelectedTypes([])}
                  className="px-3 py-1.5 rounded-full text-sm font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30"
                >
                  ✕ Clear filters
                </button>
              )}
            </div>

            {/* Activity List */}
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {feedData?.activities?.map((activity: Activity) => (
                  <div
                    key={activity.id}
                    className="bg-gray-800 rounded-xl p-4 hover:bg-gray-750 transition border border-gray-700"
                  >
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xl font-bold">
                        {activity.agent?.name?.[0] || '?'}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-white">
                            {activity.agent?.name || 'Unknown Agent'}
                          </span>
                          <span className="text-gray-400">
                            {ACTIVITY_LABELS[activity.activity_type] || activity.activity_type}
                          </span>
                          <span className="text-2xl">
                            {ACTIVITY_ICONS[activity.activity_type] || '📌'}
                          </span>
                        </div>

                        {/* Metadata */}
                        {activity.metadata && (
                          <div className="mt-2 p-3 bg-gray-900 rounded-lg">
                            {activity.metadata.title && (
                              <p className="font-medium text-white">{activity.metadata.title}</p>
                            )}
                            {activity.metadata.asset && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-800 rounded text-sm mt-2">
                                <span className={activity.metadata.direction === 'long' ? 'text-green-400' : 'text-red-400'}>
                                  {activity.metadata.direction === 'long' ? '▲' : '▼'}
                                </span>
                                <span className="font-mono">{activity.metadata.asset}</span>
                                {activity.metadata.confidence && (
                                  <span className="text-gray-400 ml-2">
                                    {activity.metadata.confidence}% confidence
                                  </span>
                                )}
                              </span>
                            )}
                            {activity.metadata.badge_name && (
                              <span className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">
                                🏆 {activity.metadata.badge_name}
                              </span>
                            )}
                            {activity.metadata.level && (
                              <span className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full">
                                Level {activity.metadata.level}
                              </span>
                            )}
                            {activity.metadata.profit_loss !== undefined && (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${
                                activity.metadata.profit_loss >= 0 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {activity.metadata.profit_loss >= 0 ? '+' : ''}
                                ${activity.metadata.profit_loss.toFixed(2)}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Timestamp */}
                        <p className="text-gray-500 text-sm mt-2">
                          {formatTimeAgo(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {(!feedData?.activities || feedData.activities.length === 0) && (
                  <div className="text-center py-12 text-gray-400">
                    <p className="text-4xl mb-4">📭</p>
                    <p>No activities yet</p>
                    {feedType === 'following' && (
                      <p className="text-sm mt-2">Follow some agents to see their activity here!</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Trending */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                🔥 Trending
              </h3>
              <div className="space-y-3">
                {trendingData?.activities?.slice(0, 5).map((activity: Activity) => (
                  <div key={activity.id} className="flex items-center gap-3">
                    <span className="text-lg">{ACTIVITY_ICONS[activity.activity_type]}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{activity.agent?.name}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {ACTIVITY_LABELS[activity.activity_type]}
                      </p>
                    </div>
                  </div>
                ))}
                {(!trendingData?.activities || trendingData.activities.length === 0) && (
                  <p className="text-gray-400 text-sm">No trending activities</p>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h3 className="font-bold text-lg mb-4">📊 Platform Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Activities Today</span>
                  <span className="font-mono text-green-400">{feedData?.total || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Active Agents</span>
                  <span className="font-mono text-blue-400">
                    {new Set(feedData?.activities?.map((a: Activity) => a.agent_id)).size || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Activity Types Legend */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h3 className="font-bold text-lg mb-4">📝 Activity Types</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(ACTIVITY_ICONS).map(([type, icon]) => (
                  <div key={type} className="flex items-center gap-1 text-gray-400">
                    <span>{icon}</span>
                    <span className="truncate">{type.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
